import React from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { ReactComponent as DollarIcon } from 'assets/icons/dollar.svg';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import FieldLabel from './FieldLabel';
import FieldError from './FieldError';
import FieldDescribe from './FieldDescribe';

type ControlledInputProps<TFormValues extends FieldValues> = {
  name: Path<TFormValues>;
  label: string | React.ReactNode;
  isRequired?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  describe?: React.ReactNode;
} & React.InputHTMLAttributes<HTMLInputElement>;

const PRICE_BASE = /[^0-9.]/g;
const SINGLE_DOT = /(\..*?)\..*/g;
const TWO_DECIMALS = /(\.\d{2}).+/g;

export const onInputOnlyNumber = (
  event: React.ChangeEvent<HTMLInputElement>,
) => {
  const { value } = event.target;
  const formatValue = value
    .replace(PRICE_BASE, '')
    .replace(SINGLE_DOT, '$1')
    .replace(TWO_DECIMALS, '$1');

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  event.target.value = formatValue || null;
};

const PriceFormInput = <TFormValues extends FieldValues>({
  name,
  label,
  className,
  describe,
  isRequired,
  disabled,
  labelClassName,
  inputClassName,
  ...props
}: ControlledInputProps<TFormValues>) => {
  const { control } = useFormContext<TFormValues>();
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });
  return (
    <div className="w-full flex flex-col gap-0.5">
      <FieldLabel
        name={name}
        className={labelClassName}
        label={label}
        isRequired={isRequired}
      />
      <div
        className={twMerge(
          'relative',
          'w-full flex justify-start flex-col shrink-0',
          className,
        )}
      >
        <div className="pointer-events-none absolute bottom-1/2 left-2.5 translate-y-1/2">
          <DollarIcon className="fill-neutral-500" />
        </div>

        <div
          className={twJoin('flex flex-col gap-0.5', disabled && 'opacity-50')}
        >
          <input
            onInput={onInputOnlyNumber}
            {...field}
            {...props}
            disabled={disabled}
            id={name}
            className={twJoin(
              inputClassName,
              'border pl-10 border-med-gray-3 font-medium text-sm h-12 placeholder-med-gray-3 placeholder:font-medium outline-none focus:border-blue-600 rounded-5 py-1.5 px-3',
              error && 'border-alert-negative bg-error-yellow',
            )}
          />
        </div>
      </div>
      {error && (
        <FieldError className="!whitespace-normal">{error?.message}</FieldError>
      )}
      {!error && describe && <FieldDescribe>{describe}</FieldDescribe>}
    </div>
  );
};

export default PriceFormInput;
